import { navigate } from 'gatsby'
import React from 'react'
import { Element } from 'react-scroll'

import { PurchaseMortgageCalculatorForm } from '../../types/calculator'
import { Realtor } from '../../types/pages'
import { purchaseMortgageCalculatorStore } from '../../utils/calculatorStore/purchase'
import { Button } from '../Button'
import { CalculatorComponent } from '../CalculatorComponent'
import { CalculatorQuestion } from '../CalculatorQuestion'
import GtmClick from '../GtmClick'
import { RealtorBox } from '../RealtorBox'

type Props = {
  heading: string
  subheading: string
  realtor?: Realtor
}

type State = PurchaseMortgageCalculatorForm & {
  questionsComplete: number
}

export class PurchaseMortgageCalculatorPage extends CalculatorComponent<Props, State> {
  totalQuestions = 7

  state = {
    questionsComplete: 0,
    buyingTimeline: null as string,
    purchasePrice: '',
    annualIncome: '',
    downPayment: '',
    monthlyPayments: '',
    creditCardDebt: '',
    creditRating: null as string,
  }

  calculate = () => {
    purchaseMortgageCalculatorStore.storeResults(this.state)

    navigate('/purchase-mortgage-calculator/results')
  }

  render = () => {
    const props = this.props
    const state = this.state

    return (
      <div className="layout-body">
        <div className="grid-center pt-8 xs-pt-4 mb-1">
          <div className="col-4_sm-9 sm-center">
            <h2 className="mb-0-5">{props.heading}</h2>
            <p className="text-large">{props.subheading}</p>
            <div className="mt-3 mb-5 sm-hidden calculator-sticky">{this.renderProgress()}</div>
          </div>
          <div className="col-12 pt-2 pb-2 mb-2 sm-visible calculator-sticky">
            <div className="grid-center">
              <div className="col-6_xs-12 text-center">{this.renderProgress()}</div>
            </div>
          </div>
          <div className="col-8_sm-9_xs-12 xs-center">
            <CalculatorQuestion
              number={1}
              question="When are you thinking of buying?"
              type="multiple-choice"
              options={[
                { value: 'asap', text: 'ASAP' },
                { value: 'next-3-months', text: 'In the next 3 months' },
                { value: 'next-6-months', text: 'In the next 3 - 6 months' },
                { value: 'no-plans', text: 'No plans, just curious' },
              ]}
              value={state.buyingTimeline}
              onChange={this.updateValue('buyingTimeline', 1)}
            />
            {state.questionsComplete >= 1 && (
              <Element id="question2">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={2}
                  question="What purchase price are you considering?"
                  subText="How much do you expect to have to pay for the home you’re interested in."
                  type="amount"
                  value={state.purchasePrice}
                  onChange={this.updateValue('purchasePrice')}
                  onNext={this.nextQuestionButtonFor(2)}
                />
              </Element>
            )}
            {state.questionsComplete >= 2 && (
              <Element id="question3">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={3}
                  question="What is your total annual income, before taxes?"
                  subText="If you’d be buying with another person, include their income too!"
                  type="amount"
                  value={state.annualIncome}
                  onChange={this.updateValue('annualIncome')}
                  onNext={this.nextQuestionButtonFor(3)}
                />
              </Element>
            )}
            {state.questionsComplete >= 3 && (
              <Element id="question4">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={4}
                  question="How much have you saved for your down payment?"
                  subText="We recommend having at least 5% of the house price."
                  type="amount"
                  value={state.downPayment}
                  onChange={this.updateValue('downPayment')}
                  onNext={this.nextQuestionButtonFor(4)}
                />
              </Element>
            )}
            {state.questionsComplete >= 4 && (
              <Element id="question5">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={5}
                  question="How much do you spend on monthly loan repayments?"
                  subText="Child support, student, personal or car loans etc."
                  type="amount"
                  value={state.monthlyPayments}
                  onChange={this.updateValue('monthlyPayments')}
                  onNext={this.nextQuestionButtonFor(5)}
                />
              </Element>
            )}
            {state.questionsComplete >= 5 && (
              <Element id="question6">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={6}
                  question="What is your total amount of credit card debt?"
                  type="amount"
                  value={state.creditCardDebt}
                  onChange={this.updateValue('creditCardDebt')}
                  onNext={this.nextQuestionButtonFor(6, true)}
                />
              </Element>
            )}
            {state.questionsComplete >= 6 && (
              <Element id="question7">
                <div className="mb-4 mt-4">
                  <div className="hr" />
                </div>
                <CalculatorQuestion
                  number={7}
                  question="How good is your credit bureau?"
                  type="multiple-choice"
                  options={[
                    { value: 'good', text: 'Good' },
                    { value: 'few-late', text: 'A few late payments' },
                    { value: 'active-collections', text: 'I have active collections' },
                    { value: 'bankrupt', text: 'I have been bankrupt' },
                  ]}
                  value={state.creditRating}
                  onChange={this.updateValue('creditRating', 7)}
                />
              </Element>
            )}
            {state.questionsComplete >= this.totalQuestions && (
              <GtmClick category="click" id={`gtm-calc-purchase-form-submit`}>
                <Button theme="green" onClick={this.calculate}>
                  Let's get your results!
                </Button>
              </GtmClick>
            )}
            {props.realtor && (
              <div className="mt-4">
                <RealtorBox {...props.realtor} />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}
